import { PayRequestPayload } from "checkout-query-client/api/types";
import { buildPostalPayloadFromGooglePay } from "checkout-query-client/utils/build-payloads/build-base-payload";

import {
  CardNetwork,
  EncryptedWallet,
  EncryptedWalletPaymentBundle,
  GooglePayAssuranceDetailsSpecification,
  GooglePayCardInfo,
  GooglePayEncryptedPaymentBundle,
  GooglePayPaymentMethodData,
  GooglePayPaymentMethodTokenizationData,
  GooglePayPaymentMethodType,
  GooglePayProtocolVersion,
  GooglePayTokenData,
  GooglePayTokenizationType,
  PayRequest,
  PaymentMethod,
  PaymentMethod_Type,
} from "frontend-client";

import { getAcceptedMandates, getDeviceFingerprintSessionId } from "../helpers";

type GooglePayloadTokenData = {
  protocolVersion: string;
  signature: string;
  signedMessage: string;
};

export const buildGooglePayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const { googlePaymentData, checkoutSessionData, formFields, orderDigest } =
    payload;

  const data = buildGooglePayRequest({ googlePaymentData });
  data.acceptedMandates = getAcceptedMandates(formFields);
  data.billingAddressFromIntentBilling = Boolean(
    checkoutSessionData?.billingAddress
  );
  data.orderDigest = orderDigest ?? "";
  data.deviceFingerprintSessionId = getDeviceFingerprintSessionId();

  return Promise.resolve(data);
};

type CardNetworkType = keyof typeof CardNetwork;

export function mapGoogleCardNetworkToProto(
  cardNetwork?: google.payments.api.CardNetwork
): CardNetwork {
  if (!cardNetwork) return CardNetwork.UNSPECIFIED;

  // Cover a case when Google and proto have different cards
  return CardNetwork[cardNetwork as CardNetworkType];
}

function mapGoogleProtocolVersion(
  protocolVersion?: string
): GooglePayProtocolVersion {
  switch (protocolVersion) {
    case "ECV1":
      return GooglePayProtocolVersion.ECV1;
    case "ECV2":
      return GooglePayProtocolVersion.ECV2;
    default:
      return GooglePayProtocolVersion.UNSPECIFIED;
  }
}

interface PayRequestProps {
  googlePaymentData?: google.payments.api.PaymentData;
}

export const buildGooglePayRequest = ({
  googlePaymentData,
}: PayRequestProps) => {
  const googlePayloadTokenData =
    googlePaymentData?.paymentMethodData?.tokenizationData?.token;
  const googlePayTokenData = googlePayloadTokenData
    ? (JSON.parse(
        googlePaymentData?.paymentMethodData?.tokenizationData?.token
      ) as GooglePayloadTokenData)
    : undefined;

  return new PayRequest({
    paymentMethod: new PaymentMethod({
      type: PaymentMethod_Type.WALLET,
      wallet: new EncryptedWallet({
        encryptedWalletPaymentBundle: new EncryptedWalletPaymentBundle({
          encryptedPaymentBundle: {
            value: new GooglePayEncryptedPaymentBundle({
              apiVersion: googlePaymentData?.apiVersion,
              apiVersionMinor: googlePaymentData?.apiVersionMinor,
              email: googlePaymentData?.email,
              shippingAddress: buildPostalPayloadFromGooglePay(
                googlePaymentData?.shippingAddress
              ),
              paymentMethodData: new GooglePayPaymentMethodData({
                type: GooglePayPaymentMethodType.CARD,
                description: googlePaymentData?.paymentMethodData.description,
                info: new GooglePayCardInfo({
                  cardDetails:
                    googlePaymentData?.paymentMethodData.info?.cardDetails,
                  assuranceDetails: new GooglePayAssuranceDetailsSpecification({
                    accountVerfied:
                      googlePaymentData?.paymentMethodData.info
                        ?.assuranceDetails?.accountVerified,
                    cardHolderAuthenticated:
                      googlePaymentData?.paymentMethodData.info
                        ?.assuranceDetails?.cardHolderAuthenticated,
                  }),
                  cardNetwork: mapGoogleCardNetworkToProto(
                    googlePaymentData?.paymentMethodData.info?.cardNetwork
                  ),
                  address: buildPostalPayloadFromGooglePay(
                    googlePaymentData?.paymentMethodData.info?.billingAddress
                  ),
                }),
                tokenizationData: new GooglePayPaymentMethodTokenizationData({
                  type: GooglePayTokenizationType.PAYMENT_GATEWAY,
                  token: new GooglePayTokenData({
                    digitalSignature: googlePayTokenData?.signature,
                    protocolVersion: mapGoogleProtocolVersion(
                      googlePayTokenData?.protocolVersion?.toUpperCase()
                    ),
                    signedMessage: googlePayTokenData?.signedMessage,
                  }),
                }),
              }),
            }),
            case: "googlePayEncryptedPaymentBundle",
          },
        }),
        billingAddress: buildPostalPayloadFromGooglePay(
          googlePaymentData?.paymentMethodData.info?.billingAddress
        ),
      }),
    }),
  });
};
